// Personnalisation des widgets publics (2026-09-23, Emeni : « on doit toujours pouvoir personnaliser le
// widget comme pour Ma Carte »). Ces réglages sont enregistrés par établissement et lus par les widgets
// collés sur les sites : changer un réglage ici met à jour le site du client sans recoller le code.
// COPIE CONFORME des validateurs du serveur (designWidgetAvis / designWidgetRealisations dans
// functions/api/[[path]].js) et des widgets publics (public/widgets/*.js) — modifier les trois ensemble.

export const WIDGET_POLICES = [
    { v: '', label: 'Celle du site' },
    { v: 'Inter', label: 'Inter' },
    { v: 'Poppins', label: 'Poppins' },
    { v: 'Montserrat', label: 'Montserrat' },
    { v: 'Space Grotesk', label: 'Space Grotesk' },
    { v: 'Playfair Display', label: 'Playfair Display' },
    { v: 'DM Serif Display', label: 'DM Serif Display' },
    { v: 'Lora', label: 'Lora' },
    { v: 'Bebas Neue', label: 'Bebas Neue' },
    { v: 'Caveat', label: 'Caveat' },
];

export const WIDGET_THEMES = [{ v: 'clair', label: 'Clair' }, { v: 'sombre', label: 'Sombre' }];
export const WIDGET_ARRONDIS = [{ v: 'carre', label: 'Carré' }, { v: 'arrondi', label: 'Arrondi' }, { v: 'rond', label: 'Très arrondi' }];
export const WIDGET_OMBRES = [{ v: 'aucune', label: 'Aucune' }, { v: 'douce', label: 'Douce' }, { v: 'marquee', label: 'Marquée' }];
export const WIDGET_FONDS = [
    { v: 'creme', label: 'Crème (teinte de la couleur)' },
    { v: 'blanc', label: 'Blanc' },
    { v: 'teinte', label: 'Teinté' },
];
export const WIDGET_FORMATS = [
    { v: '4/3', label: 'Paysage (4:3)' },
    { v: '1/1', label: 'Carré' },
    { v: '4/5', label: 'Portrait (4:5)' },
    { v: '16/9', label: 'Panoramique (16:9)' },
];
export const AVIS_STYLES = [
    { v: 'liste', label: 'Liste', hint: 'Les avis les uns sous les autres.' },
    { v: 'carrousel', label: 'Carrousel', hint: 'Des cartes qui défilent horizontalement.' },
    { v: 'badge', label: 'Badge compact', hint: 'La note et le nombre d\'avis, rien de plus.' },
    { v: 'panneau', label: 'Panneau de réputation', hint: 'Note, répartition par étoile et derniers avis.' },
];

export interface DesignCommun {
    theme: 'clair' | 'sombre';
    couleur: string;
    police: string;
    arrondi: 'carre' | 'arrondi' | 'rond';
    ombre: 'aucune' | 'douce' | 'marquee';
    largeur: number;
}
// Simulateur de devis (2026-10-10) : même socle + fond de la carte. Ombre « douce » par défaut (relief d'origine).
export interface DesignSimulateur extends DesignCommun {
    fond: 'creme' | 'blanc' | 'teinte';
}
export interface DesignAvis extends DesignCommun {
    titre: string;
    note: boolean;
    nombre: number;
    commentaire_seul: boolean;
    reponses: boolean;
    // Mention « Comment ces avis sont collectés » — obligation légale (art. L111-7-2 du Code de la
    // consommation). Texte vide = celui construit par le serveur, qui dit la vérité du produit.
    transparence: boolean;
    transparence_texte: string;
    // Badge « Vérifié » sur les avis rattachés à une intervention enregistrée (2026-09-23).
    verifies: boolean;
}
export interface DesignRealisations extends DesignCommun {
    titre: string;
    sous_titre: string;
    colonnes: number;
    nombre: number;
    format: string;
    date: boolean;
    mention: boolean;
    mention_texte: string;
}

export interface DesignMessagerie extends DesignCommun {
    titre: string;
    intro: string;
    bouton: string;
    champs: string[];
    consent_texte: string;
    merci_texte: string;
    // Qui peut écrire : conditionne l'affichage du sélecteur particulier/professionnel sur le formulaire
    // (widget externe ET bloc "Formulaire de contact" de Ma Carte, qui lit ce même réglage).
    contact_type_mode: 'les_deux' | 'particulier' | 'professionnel';
    // Case newsletter (2026-09-25, finalité distincte du message → consentement séparé, RGPD art. 5.1.b) :
    // désactivée par défaut, jamais précochée quand activée. Un lead à part (carte_leads.kind='newsletter')
    // est créé si cochée, même mécanisme que le bloc Newsletter de Ma Carte.
    newsletter_actif: boolean;
    newsletter_texte: string;
}

export const MESSAGERIE_CHAMPS = [
    { v: 'phone', label: 'Téléphone' },
    { v: 'address', label: 'Adresse' },
    { v: 'subject', label: 'Objet' },
];

export const MENTION_REALISATIONS_DEFAUT = 'Photos prises sur nos interventions, avant et après — même cadrage, même lumière, sans retouche.';

const commun = (d: any): DesignCommun => ({
    theme: d?.theme === 'sombre' ? 'sombre' : 'clair',
    couleur: typeof d?.couleur === 'string' && /^#[0-9a-fA-F]{6}$/.test(d.couleur) ? d.couleur : '',
    police: WIDGET_POLICES.some((p) => p.v === d?.police) ? d.police : '',
    arrondi: ['carre', 'arrondi', 'rond'].includes(d?.arrondi) ? d.arrondi : 'arrondi',
    ombre: ['aucune', 'douce', 'marquee'].includes(d?.ombre) ? d.ombre : 'aucune',
    largeur: Math.max(0, Math.min(1600, Math.round(Number(d?.largeur) || 0))),
});

export const designSimulateur = (d: any): DesignSimulateur => ({
    ...commun(d),
    ombre: ['aucune', 'douce', 'marquee'].includes(d?.ombre) ? d.ombre : 'douce',
    fond: ['creme', 'blanc', 'teinte'].includes(d?.fond) ? d.fond : 'creme',
});

export const designAvis = (d: any): DesignAvis => ({
    ...commun(d),
    titre: typeof d?.titre === 'string' ? d.titre.slice(0, 80) : 'Avis clients',
    note: d?.note !== false,
    nombre: Math.max(0, Math.min(50, Math.round(Number(d?.nombre) || 0))),
    commentaire_seul: d?.commentaire_seul === true,
    reponses: d?.reponses !== false,
    transparence: d?.transparence !== false,
    transparence_texte: typeof d?.transparence_texte === 'string' ? d.transparence_texte.slice(0, 600) : '',
    verifies: d?.verifies !== false,
});

export const designRealisations = (d: any): DesignRealisations => ({
    ...commun(d),
    titre: typeof d?.titre === 'string' ? d.titre.slice(0, 80) : '',
    sous_titre: typeof d?.sous_titre === 'string' ? d.sous_titre.slice(0, 200) : '',
    colonnes: Math.max(1, Math.min(4, Math.round(Number(d?.colonnes) || 3))),
    nombre: Math.max(0, Math.min(200, Math.round(Number(d?.nombre) || 0))),
    format: WIDGET_FORMATS.some((f) => f.v === d?.format) ? d.format : '4/3',
    date: d?.date !== false,
    mention: d?.mention !== false,
    mention_texte: typeof d?.mention_texte === 'string' && d.mention_texte.trim() ? d.mention_texte.slice(0, 200) : MENTION_REALISATIONS_DEFAUT,
});

export const designMessagerie = (d: any): DesignMessagerie => ({
    ...commun(d),
    titre: typeof d?.titre === 'string' ? d.titre.slice(0, 80) : 'Contactez-nous',
    intro: typeof d?.intro === 'string' ? d.intro.slice(0, 300) : '',
    bouton: typeof d?.bouton === 'string' && d.bouton.trim() ? d.bouton.slice(0, 40) : 'Envoyer',
    champs: Array.isArray(d?.champs) ? d.champs.filter((f: any) => MESSAGERIE_CHAMPS.some((c) => c.v === f)) : [],
    consent_texte: typeof d?.consent_texte === 'string' ? d.consent_texte.slice(0, 300) : '',
    merci_texte: typeof d?.merci_texte === 'string' && d.merci_texte.trim() ? d.merci_texte.slice(0, 200) : 'Merci ! Votre message est bien arrivé, nous vous répondons vite.',
    contact_type_mode: ['les_deux', 'particulier', 'professionnel'].includes(d?.contact_type_mode) ? d.contact_type_mode : 'particulier',
    newsletter_actif: d?.newsletter_actif === true,
    newsletter_texte: typeof d?.newsletter_texte === 'string' ? d.newsletter_texte.slice(0, 300) : '',
});
